import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";
import { ApiContext, useApiContext } from "contexts/ApiProvider";
import ReactMarkdown from "react-markdown";

// Define a type for chat messages
interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

// Styled components for better visual separation
const StyledChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "500px", // Adjust height as needed
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const StyledChatMessages = styled(List)(({ theme }) => ({
  flexGrow: 1,
  overflowY: "auto",
  padding: theme.spacing(2),
}));

const StyledChatMessage = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  "&.user": {
    textAlign: "right",
  },
  "&.assistant": {
    textAlign: "left",
  },
}));

const StyledMessageContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  maxWidth: "80%",
  display: "inline-block",
  "&.user": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  "&.assistant": {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
  },
}));

const StyledChatInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flexGrow: 1,
  marginRight: theme.spacing(2),
}));

const ChatComponent: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { aiApi } = useApiContext();

  // Simulate API call to a LLM Chatbot
  const sendMessage = async (message: string) => {
    setIsLoading(true);
    try {
      // Add user message to the chat
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: "user", content: message },
      ]);
      setInput("");

      // Simulate API call delay
      //await new Promise((resolve) => setTimeout(resolve, 1000));
      // Simulate response from the LLM Chatbot
      //const response = await getChatbotResponse(message);

      const response = await aiApi.getChatbotResponse(message);

      // Add assistant's response to the chat
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: "assistant", content: response },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble understanding you right now. Please try again later.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to simulate LLM Chatbot response
  const getChatbotResponse = async (userMessage: string): Promise<string> => {
    // Replace this with actual API call to your LLM Chatbot
    // This is a placeholder for demonstration purposes
    const responses = [
      `You said: "${userMessage}". That's interesting!`,
      `I understand you're talking about: "${userMessage}".`,
      `Tell me more about: "${userMessage}".`,
      `That's a great point about: "${userMessage}".`,
      `How does "${userMessage}" relate to the bigger picture?`,
    ];
    const randomIndex = Math.floor(Math.random() * responses.length);
    return responses[randomIndex];
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  };

  const handleSendMessage = () => {
    if (input.trim() !== "") {
      sendMessage(input);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <StyledChatContainer>
      <StyledChatMessages>
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <StyledChatMessage className={message.role}>
              <StyledMessageContent className={message.role}>
                {message.role === "user" && (
                  <Typography variant="body1">{message.content}</Typography>
                )}
                {message.role === "assistant" && (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                )}
              </StyledMessageContent>
            </StyledChatMessage>
            {index < messages.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        {isLoading && (
          <StyledChatMessage className="assistant">
            <StyledMessageContent className="assistant">
              <CircularProgress size={20} />
            </StyledMessageContent>
          </StyledChatMessage>
        )}
        <div ref={messagesEndRef} />
      </StyledChatMessages>
      <StyledChatInput>
        <StyledTextField
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          variant="outlined"
          disabled={isLoading}
        />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={isLoading}
        >
          Send
        </Button>
      </StyledChatInput>
    </StyledChatContainer>
  );
};

export default ChatComponent;
